import { makeStyles } from "@material-ui/core";

const rootKeyframes = `
@keyframes ccw-fadeSlideUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ccw-pulseGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(0,221,158,0.0); }
  50%      { box-shadow: 0 0 12px 2px rgba(0,221,158,0.18); }
}
@keyframes ccw-progressGrow {
  from { width: 0%; }
}
@keyframes ccw-countPulse {
  0%   { transform: scale(0.85); opacity: 0; }
  60%  { transform: scale(1.04); }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes ccw-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes ccw-badgePulse {
  0%, 100% { border-color: #00DD9E; }
  50%      { border-color: rgba(0,221,158,0.45); }
}
@keyframes ccw-dotPulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.35); }
}
@keyframes ccw-iconGlow {
  0%, 100% { filter: drop-shadow(0 0 0px rgba(255,255,255,0)); }
  50%      { filter: drop-shadow(0 0 6px rgba(255,255,255,0.35)); }
}
`;

if (typeof document !== "undefined") {
  const styleId = "ccw-root-keyframes";
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = rootKeyframes;
    document.head.appendChild(styleEl);
  }
}

const borderCard = "1.5px solid rgba(0, 180, 230, 0.30)";

const useStyles = makeStyles({
  container: {
    background: "transparent",
    borderRadius: 20,
    padding: 24,
    color: "#fff",
    fontFamily: "Montserrat, sans-serif",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    animation: "ccw-fadeSlideUp 0.5s ease both",
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: "#00DD9E",
    margin: 0,
    fontFamily: "MontserratBold, Montserrat, sans-serif",
  },
  activeBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    border: "1.5px solid #00DD9E",
    color: "#00DD9E",
    borderRadius: 20,
    padding: "4px 14px",
    fontSize: 13,
    fontWeight: 600,
    animation: "ccw-badgePulse 3s ease-in-out infinite",
    transition: "background 0.3s ease, transform 0.2s ease",
    cursor: "default",
    "&:hover": {
      background: "rgba(0,221,158,0.10)",
      transform: "scale(1.05)",
    },
  },

  summaryCard: {
    background: "hsl(219.67deg 89.63% 26.47%)",
    border: borderCard,
    borderRadius: 14,
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    animation: "ccw-fadeSlideUp 0.55s ease both",
    animationDelay: "0.08s",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
    },
  },
  summaryLabel: {
    fontSize: 13,
    color: "rgba(255,255,255,0.7)",
    marginBottom: 4,
    textAlign: "left",
  },
  summaryValue: {
    fontSize: 22,
    fontWeight: 700,
    color: "#fff",
  },
  summaryRight: {
    textAlign: "right",
  },

  cardsRow: {
    display: "flex",
    gap: 14,
    marginBottom: 16,
    "@media (max-width: 560px)": {
      flexDirection: "column",
    },
  },
  card: {
    flex: 1,
    background: "hsl(200.39deg 96.23% 31.18%)",
    border: borderCard,
    borderRadius: 14,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    animation: "ccw-fadeSlideUp 0.6s ease both",
    animationDelay: "0.16s",
    transition:
      "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.3s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 8px 28px rgba(0,0,0,0.3)",
      borderColor: "rgba(0, 221, 158, 0.35)",
    },
    "&:nth-child(2)": {
      animationDelay: "0.24s",
    },
  },
  cardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
    color: "rgba(255,255,255,0.85)",
  },

  infoBtn: {
    padding: 4,
    color: "rgba(255,255,255,0.55)",
    transition: "all 0.3s ease",
    animation: "ccw-iconGlow 3s ease-in-out infinite",
    "&:hover": {
      color: "#fff",
      background: "rgba(255,255,255,0.08)",
      transform: "scale(1.15)",
    },
  },

  bigNumberGreen: {
    fontSize: 64,
    fontWeight: 700,
    lineHeight: 1,
    color: "#00DD9E",
    textAlign: "left",
    fontFamily: "MontserratBold, Montserrat, sans-serif",
    animation: "ccw-countPulse 0.6s ease both",
    animationDelay: "0.35s",

    "@media (max-width: 400px)": {
      fontSize: 48,
    },
  },
  bigNumberWhite: {
    fontSize: 64,
    fontWeight: 700,
    lineHeight: 1,
    color: "#fff",
    textAlign: "left",
    fontFamily: "MontserratBold, Montserrat, sans-serif",
    animation: "ccw-countPulse 0.6s ease both",
    animationDelay: "0.45s",
    "@media (max-width: 400px)": {
      fontSize: 48,
    },
  },

  progressRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: "rgba(255,255,255,0.6)",
  },
  progressTrack: {
    flex: 1,
    height: 4,
    background: "rgba(255,255,255,0.15)",
    borderRadius: 2,
    overflow: "hidden",
  },
  progressFillGreen: {
    height: "100%",
    background: "linear-gradient(90deg, #00DD9E, #00f5b0)",
    borderRadius: 2,
    animation: "ccw-progressGrow 1s ease both",
    animationDelay: "0.5s",
  },
  progressFillWhite: {
    height: "100%",
    background: "rgba(255,255,255,0.7)",
    borderRadius: 2,
    animation: "ccw-progressGrow 1s ease both",
    animationDelay: "0.6s",
  },

  tapDetails: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    color: "#00DD9E",
    cursor: "pointer",
    transition: "gap 0.2s ease, opacity 0.2s ease",
    "&:hover": {
      gap: 7,
      opacity: 0.8,
    },
  },

  buyMoreBtn: {
    background: "transparent",
    border: "1.5px solid rgba(255,255,255,0.4)",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 0",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    fontFamily: "MontserratBold, Montserrat, sans-serif",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      background: "rgba(0,221,158,0.12)",
      borderColor: "#00DD9E",
      color: "#00DD9E",
      transform: "scale(1.02)",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },

  activityCard: {
    background: "hsl(200.39deg 96.23% 31.18%)",
    border: borderCard,
    borderRadius: 14,
    padding: "16px 18px",
    animation: "ccw-fadeSlideUp 0.65s ease both",
    animationDelay: "0.32s",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
    },
  },
  activityTitle: {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 14,
    color: "rgba(255,255,255,0.9)",
    textAlign: "left",
  },
  activityItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 14,
    transition: "background 0.2s ease, padding-left 0.2s ease",
    borderRadius: 8,
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    marginBottom: 10,
    "&:hover": {
      background: "rgba(255,255,255,0.04)",
      paddingLeft: 6,
    },
    "&:last-child": {
      paddingBottom: 0,
      borderBottom: "none",
    },
  },
  activityLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  dotGreen: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#00DD9E",
    flexShrink: 0,
    animation: "ccw-dotPulse 2.5s ease-in-out infinite",
    boxShadow: "0 0 6px rgba(0,221,158,0.4)",
  },
  dotWhite: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.5)",
    flexShrink: 0,
  },
  activityType: {
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    textAlign: "left",
  },
  activityDate: {
    fontSize: 12,
    color: "rgba(255,255,255,0.55)",
    marginTop: 2,
  },
  amountGreen: {
    fontSize: 15,
    fontWeight: 700,
    color: "#00DD9E",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  amountWhite: {
    fontSize: 15,
    fontWeight: 700,
    color: "#fff",
    transition: "transform 0.2s ease",
  },
  tooltipContent: {
    fontSize: 12,
    lineHeight: 1.5,
    color: "#FFFFFF",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    letterSpacing: "0.3px",
  },

  tooltipWrapper: {
    backgroundColor: "transparent",
  },

  "@global": {
    ".MuiTooltip-tooltip": {
      backgroundColor: "rgba(13, 42, 74, 0.8)",
      // backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      border: "1px solid rgba(255, 255, 255, 0.15)",
      borderRadius: "10px",
      padding: "12px 16px",
      boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.3)",
      maxWidth: 280,
    },
    ".MuiTooltip-arrow": {
      color: "rgba(13, 42, 74, 0.8)",
      backdropFilter: "blur(8px)",
    },
  },
});
export default useStyles;
